// Enhanced mention utilities and hooks

import { useState } from 'react';
import { UserSearchType } from '../UserSearch/UserSearch';

interface MentionData {
  start: number;
  end: number;
  username: string;
  userId: string;
  isComplete: boolean;
}

interface CurrentMention {
  start: number;
  searchQuery: string;
  isActive: boolean;
}

const completeMentionRegex = /::([a-zA-Z0-9_-]+)::/g;

// Utility functions for mention handling
export const mentionUtils = {
  /**
   * Check if cursor is at the end of a mention element
   */
  isCursorAtMentionEnd: (editor: HTMLDivElement | null): { isMention: boolean; mentionElement: Element | null } => {
    if (!editor) return { isMention: false, mentionElement: null };

    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) return { isMention: false, mentionElement: null };

    const range = selection.getRangeAt(0);
    const { startContainer, startOffset } = range;

    // Check if we're at the end of a text node that's inside a mention
    if (startContainer.nodeType === Node.TEXT_NODE) {
      const textNode = startContainer as Text;
      const parentElement = textNode.parentElement;

      // Check if parent is a mention and we're at the end of the text
      if (parentElement?.classList.contains('mention') && startOffset === textNode.textContent?.length) {
        return { isMention: true, mentionElement: parentElement };
      }
    }

    // Check if we're positioned right after a mention element
    if (startContainer.nodeType === Node.ELEMENT_NODE) {
      const element = startContainer as Element;
      const previousNode = startOffset > 0 ? element.childNodes[startOffset - 1] : null;

      if (previousNode?.nodeType === Node.ELEMENT_NODE) {
        const prevElement = previousNode as Element;
        if (prevElement.classList.contains('mention')) {
          return { isMention: true, mentionElement: prevElement };
        }
      }
    }

    return { isMention: false, mentionElement: null };
  },

  /**
   * Extract all mentions from text, both complete (::userId::) and incomplete (@username)
   */
  extractAllMentions: (text: string): MentionData[] => {
    const mentions: MentionData[] = [];

    // Find complete mentions (::userId::)

    let match;

    while ((match = completeMentionRegex.exec(text)) !== null) {
      mentions.push({
        start: match.index,
        end: match.index + match[0].length,
        username: match[1],
        userId: match[1],
        isComplete: true,
      });
    }

    // Find incomplete mentions (@username)
    const incompleteMentionRegex = /(^|\s)@([^\s@:]+)/g;
    while ((match = incompleteMentionRegex.exec(text)) !== null) {
      const mentionStart = match.index + match[1].length;
      mentions.push({
        start: mentionStart,
        end: mentionStart + 1 + match[2].length,
        username: match[2],
        userId: match[2],
        isComplete: false,
      });
    }

    return mentions.sort((a, b) => a.start - b.start);
  },

  /**
   * Find current mention being typed based on cursor position
   */
  findCurrentMention: (text: string, cursorPosition: number): CurrentMention | null => {
    const textBeforeCursor = text.substring(0, cursorPosition);
    // Updated regex to properly match @ at the start of the string
    const currentMentionRegex = /(^|\s)@([^\s@:]*)$/;
    const currentMatch = currentMentionRegex.exec(textBeforeCursor);

    if (currentMatch) {
      // Calculate start position correctly
      // currentMatch.index gives the position of the match start (which includes the ^ or \s)
      // currentMatch[1] is the captured group (^ or \s), so we need its length to get to the @
      const startPosition = currentMatch.index + currentMatch[1].length;

      return {
        start: startPosition,
        searchQuery: currentMatch[2],
        isActive: true,
      };
    }

    return null;
  },

  /**
   * Delete mentions at cursos when pressing Backspace
   */
  deleteMentionAtCursor: (html: string, cursorPosition: number): { newHtml: string; newCursorPosition: number } => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    const mentions = Array.from(tempDiv.querySelectorAll('.mention'));
    if (mentions.length === 0) {
      return { newHtml: html, newCursorPosition: cursorPosition };
    }

    const lastMention = mentions[mentions.length - 1];
    const mentionText = lastMention.textContent || '';

    // Build flat text and map nodes
    let flatText = '';
    const nodeOffsetMap: { node: Node; start: number; end: number }[] = [];

    const walker = document.createTreeWalker(tempDiv, NodeFilter.SHOW_TEXT, null);
    let node;
    while ((node = walker.nextNode())) {
      const text = node.textContent || '';
      const start = flatText.length;
      const end = start + text.length;
      nodeOffsetMap.push({ node, start, end });
      flatText += text;
    }

    // Calculate offset of lastMention in flat text
    let mentionStart = -1;
    let mentionEnd = -1;

    // Look for the text node inside the last mention and map its offset
    const mentionWalker = document.createTreeWalker(lastMention, NodeFilter.SHOW_TEXT, null);
    const mentionTextNode = mentionWalker.nextNode() as Text | null;
    if (mentionTextNode) {
      const mapEntry = nodeOffsetMap.find(m => m.node === mentionTextNode);
      if (mapEntry) {
        mentionStart = mapEntry.start;
        mentionEnd = mapEntry.start + (mentionTextNode.textContent?.length || 0);
      }
    }

    if (mentionStart === -1 || mentionEnd === -1) {
      return { newHtml: html, newCursorPosition: cursorPosition };
    }

    const isCursorAtMentionEnd = cursorPosition === mentionEnd;

    if (isCursorAtMentionEnd) {
      const remainingText = flatText.slice(mentionEnd).trim();
      if (remainingText.length === 0) {
        lastMention.remove();
        return {
          newHtml: tempDiv.innerHTML,
          newCursorPosition: mentionStart,
        };
      }
    }

    return { newHtml: html, newCursorPosition: cursorPosition };
  },

  /**
   * Replace mention text with formatted mention in HTML
   */
  replaceMentionAtPosition: (
    html: string,
    startPos: number,
    searchLength: number,
    userId: string,
    userName: string
  ): string => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Find the text node containing the mention
    const walker = document.createTreeWalker(tempDiv, NodeFilter.SHOW_TEXT, null);
    let currentPos = 0;
    let targetNode: Text | null = null;
    let relativeStart = startPos;

    let node;
    while ((node = walker.nextNode())) {
      const textNode = node as Text;
      const nodeLength = textNode.textContent?.length || 0;

      if (currentPos + nodeLength > startPos) {
        targetNode = textNode;
        relativeStart = startPos - currentPos;
        break;
      }
      currentPos += nodeLength;
    }

    if (targetNode) {
      const textContent = targetNode.textContent || '';
      const beforeMention = textContent.substring(0, relativeStart);
      const afterMention = textContent.substring(relativeStart + searchLength + 1); // +1 for @

      // Create mention HTML
      const mentionHtml = `<strong class="mention" data-mention="${userId}">@${userName}</strong>`;

      // Replace the text node with the new content
      const wrapper = document.createElement('span');
      wrapper.innerHTML = beforeMention + mentionHtml + ' ' + afterMention;

      const parent = targetNode.parentNode;
      if (parent) {
        while (wrapper.firstChild) {
          parent.insertBefore(wrapper.firstChild, targetNode);
        }
        parent.removeChild(targetNode);
      }
    }

    return tempDiv.innerHTML;
  },

  /**
   * Convert ::userId:: mentions to HTML format
   */
  formatMentionsToHtml: (
    content: string,
    users: any[] = [],
    fallbackUser?: { userId: string; name: string },
    className?: string
  ): string => {
    const getUserName = (userId: string) => {
      if (!Array.isArray(users)) return 'Unknown User';
      const match = users.find((u: any) => u.userId === userId);

      if (match) {
        const name = match.name || match.username || 'Unknown User';
        // Add (deleted) label if user is deleted
        if (match.deleted === true) {
          return `${name} (deleted)`;
        }
        return name;
      }
      // Check fallback user (e.g., the comment author who might be deactivated)
      if (fallbackUser && fallbackUser.userId === userId) {
        return fallbackUser.name || userId;
      }
      // Return userId as fallback if user not found
      return 'Unknown User (deleted)';
    };

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;

    const walker = document.createTreeWalker(tempDiv, NodeFilter.SHOW_TEXT, null);
    const textNodes: Text[] = [];
    let node;
    while ((node = walker.nextNode())) {
      textNodes.push(node as Text);
    }

    textNodes.forEach(textNode => {
      const text = textNode.textContent || '';
      const mentionPattern = /::([a-zA-Z0-9_-]+)::/g;
      let match;
      let newHTML = '';
      let lastIndex = 0;
      let replaced = false;

      while ((match = mentionPattern.exec(text)) !== null) {
        const userId = match[1];
        const userName = getUserName(userId);
        const mentionClass = className || 'mention';
        newHTML += text.slice(lastIndex, match.index);
        newHTML += `<strong class="${mentionClass}" data-mention="${userId}">@${userName}</strong>`;
        lastIndex = mentionPattern.lastIndex;
        replaced = true;
      }

      if (replaced) {
        newHTML += text.slice(lastIndex);
        const wrapper = document.createElement('span');
        wrapper.innerHTML = newHTML;
        const parent = textNode.parentNode;
        if (parent) {
          while (wrapper.firstChild) {
            parent.insertBefore(wrapper.firstChild, textNode);
          }
          parent.removeChild(textNode);
        }
      }
    });

    return tempDiv.innerHTML;
  },

  /**
   * Extract user IDs from formatted mentions in HTML
   */
  extractUserIdsFromHtml: (html: string): string[] => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    const mentions = tempDiv.querySelectorAll('[data-mention]');
    return Array.from(mentions).map(mention => mention.getAttribute('data-mention') || '');
  },

  /**
   * Convert HTML mentions back to ::userId:: format for storage
   */
  convertHtmlMentionsToStorage: (html: string): string => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    const mentions = tempDiv.querySelectorAll('[data-mention]');

    mentions.forEach(mention => {
      const userId = mention.getAttribute('data-mention');
      if (userId) {
        const textNode = document.createTextNode(`::${userId}::`);
        mention.parentNode?.replaceChild(textNode, mention);
      }
    });

    return tempDiv.innerHTML;
  },

  /**
   * Get cursor position in plain text from selection
   */
  getCursorPosition: (editor: HTMLDivElement | null): number => {
    if (!editor) return 0;

    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) return 0;

    const range = selection.getRangeAt(0);
    if (!editor.contains(range.startContainer)) return 0;

    // Create a range from start of editor to cursor
    const preRange = document.createRange();
    preRange.setStart(editor, 0);
    preRange.setEnd(range.startContainer, range.startOffset);

    return preRange.toString().length;
  },

  /**
   * Set cursor position in editor
   */
  setCursorPosition: (editor: HTMLDivElement | null, position: number): void => {
    if (!editor) return;

    const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT, null);
    let currentPos = 0;
    let node;

    while ((node = walker.nextNode())) {
      const textNode = node as Text;
      const nodeLength = textNode.textContent?.length || 0;

      if (currentPos + nodeLength >= position) {
        const range = document.createRange();
        const offset = Math.min(position - currentPos, nodeLength);
        range.setStart(textNode, offset);
        range.setEnd(textNode, offset);

        const selection = window.getSelection();
        if (selection) {
          selection.removeAllRanges();
          selection.addRange(range);
        }
        break;
      }
      currentPos += nodeLength;
    }
  },
};

// Custom hook for mention handling
export const useMentionHandler = (richTextEditorRef: React.RefObject<any>, users: any[] = []) => {
  const [userSearchOpen, setUserSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [lastAtPosition, setLastAtPosition] = useState(-1);
  const [detectedMentions, setDetectedMentions] = useState<MentionData[]>([]);

  const handleTextChange = (html: string, plainText: string) => {
    const cursorPosition = mentionUtils.getCursorPosition(richTextEditorRef.current?.getEditor());

    // Find current mention being typed
    const currentMention = mentionUtils.findCurrentMention(plainText, cursorPosition);

    if (currentMention) {
      setUserSearchOpen(true);
      setLastAtPosition(currentMention.start);
      setSearchText(currentMention.searchQuery);
    } else {
      setUserSearchOpen(false);
      setLastAtPosition(-1);
      setSearchText('');
    }

    // Extract all mentions from the text
    const allMentions = mentionUtils.extractAllMentions(plainText);
    setDetectedMentions(allMentions);

    return {
      currentMention,
      allMentions,
      userSearchOpen,
      searchText,
    };
  };

  const handleUserSelect = (user: UserSearchType) => {
    if (!richTextEditorRef.current) return;

    const editor = richTextEditorRef.current.getEditor();
    if (!editor) return;

    try {
      // Get current content
      const currentHtml = richTextEditorRef.current.getValue();
      const currentPlainText = editor.textContent || '';

      // Check if we're using the external button (no @ typed)
      const isExternalButtonClick = lastAtPosition < 0;
      const isEmpty = !currentPlainText.trim();

      // For external button clicks, insert mention at cursor position or end
      if (isExternalButtonClick) {
        // Create mention HTML
        const mentionHtml = `<strong class="mention" data-mention="${user.userId}">@${user.name}</strong>&nbsp;`;

        if (isEmpty) {
          // For empty content, just set the mention HTML
          richTextEditorRef.current.setValue(mentionHtml);
        } else {
          // For existing content, insert at current cursor position
          const cursorPos = mentionUtils.getCursorPosition(editor);
          const textBefore = currentPlainText.substring(0, cursorPos);
          const textAfter = currentPlainText.substring(cursorPos);

          // Build new HTML by inserting mention at cursor
          // Simple approach: append at end if cursor detection fails
          if (cursorPos > 0 && cursorPos < currentPlainText.length) {
            // Insert at cursor - use a simpler approach
            richTextEditorRef.current.insertText(`@${user.name} `);
            // Then convert to proper mention format
            const newHtml = richTextEditorRef.current.getValue();
            const formattedHtml = newHtml.replace(
              new RegExp(`@${user.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s?`),
              `<strong class="mention" data-mention="${user.userId}">@${user.name}</strong>&nbsp;`
            );
            richTextEditorRef.current.setValue(formattedHtml);
          } else {
            // Append at end
            richTextEditorRef.current.setValue(currentHtml + mentionHtml);
          }
        }

        // Focus and move cursor to end after a short delay
        setTimeout(() => {
          const length = editor.textContent?.length || 0;
          mentionUtils.setCursorPosition(editor, length);
          editor.focus();
        }, 10);

        // Reset search state
        setUserSearchOpen(false);
        setLastAtPosition(-1);
        setSearchText('');
        return;
      }

      // Standard flow for typed @ mentions
      const insertPosition = lastAtPosition;
      const replaceLength = searchText.length;

      // Create the new HTML with the mention
      const newHtml = mentionUtils.replaceMentionAtPosition(
        currentHtml,
        insertPosition,
        replaceLength,
        user.userId,
        user.name || user.userId
      );

      // Set the new content
      richTextEditorRef.current.setValue(newHtml);

      // Position cursor after the mention
      const newCursorPos = insertPosition + user.name.length + 2; // +2 for @ and space
      setTimeout(() => {
        mentionUtils.setCursorPosition(editor, newCursorPos);
        editor.focus();
      }, 10);
    } catch (error) {
      console.error('Error handling user selection:', error);
      // Fallback: just insert at current cursor
      if (richTextEditorRef.current.insertText) {
        richTextEditorRef.current.insertText(`@${user.name} `);
      }
    }

    // Reset search state
    setUserSearchOpen(false);
    setLastAtPosition(-1);
    setSearchText('');
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Backspace') {
      const editor = richTextEditorRef.current?.getEditor();
      if (!editor) return false;

      const currentHtml = richTextEditorRef.current.getValue();
      const cursorPosition = mentionUtils.getCursorPosition(editor);

      const { newHtml, newCursorPosition } = mentionUtils.deleteMentionAtCursor(currentHtml, cursorPosition);

      if (newHtml !== currentHtml) {
        e.preventDefault(); // Prevent default backspace
        richTextEditorRef.current.setValue(newHtml);

        setTimeout(() => {
          mentionUtils.setCursorPosition(editor, newCursorPosition);
          editor.focus();
        }, 0);

        setUserSearchOpen(false);
        setSearchText('');
        setLastAtPosition(-1);
        return true;
      }
    }

    if (userSearchOpen && e.key === 'Escape') {
      e.preventDefault();
      setUserSearchOpen(false);
      setSearchText('');
      setLastAtPosition(-1);
      return true;
    }

    return false;
  };

  return {
    userSearchOpen,
    searchText,
    lastAtPosition,
    detectedMentions,
    handleTextChange,
    handleUserSelect,
    handleKeyDown,
    setUserSearchOpen,
    setSearchText,
    setLastAtPosition,
  };
};
